import { entityMetadataSchema } from "../domain/common";
import type {
  AuditEvent,
  BehaviorCorrection,
  ConstitutionVersion,
  DecisionJournalEntry,
  EvidenceStatement,
  ExternalSummaryRecord,
  IdeaParkingItem,
  JointCorrection,
  PromiseEntry,
  StructuredCEORecord,
} from "../domain/types";
import { RuleErrorCode, assertNoIssues, type ValidationIssue } from "./errors";

export interface StatementValidationContext {
  statementsById?: ReadonlyMap<string, EvidenceStatement>;
  forExternalExport?: boolean;
}

function issue(code: ValidationIssue["code"], path: string, message: string): ValidationIssue {
  return { code, path, message };
}

export function validateEntityMetadata(value: unknown): ValidationIssue[] {
  const parsed = entityMetadataSchema.safeParse(value);
  if (parsed.success) {
    return [];
  }
  return parsed.error.issues.map((item) =>
    issue(RuleErrorCode.SCHEMA_VERSION_REQUIRED, item.path.join("."), item.message),
  );
}

export function validateEvidenceStatement(
  statement: EvidenceStatement,
  context: StatementValidationContext = {},
): ValidationIssue[] {
  const issues = validateEntityMetadata(statement);
  if (
    statement.confidence !== undefined &&
    (statement.confidence < 0 || statement.confidence > 1)
  ) {
    issues.push(
      issue(RuleErrorCode.CONFIDENCE_OUT_OF_RANGE, "confidence", "置信度必须位于 0 到 1 之间。"),
    );
  }

  if (
    statement.statementType === "fact" &&
    statement.generatedBy === "ai" &&
    statement.userConfirmation !== "confirmed" &&
    statement.userConfirmation !== "corrected"
  ) {
    issues.push(
      issue(
        RuleErrorCode.AI_FACT_REQUIRES_CONFIRMATION,
        "userConfirmation",
        "AI 生成内容必须经用户确认或修正后才能成为 fact。",
      ),
    );
  }

  if (
    statement.statementType === "fact" &&
    statement.evidenceRefs.length === 0 &&
    statement.userConfirmation !== "confirmed" &&
    statement.userConfirmation !== "corrected"
  ) {
    issues.push(
      issue(
        RuleErrorCode.FACT_REQUIRES_EVIDENCE_OR_CONFIRMATION,
        "evidenceRefs",
        "fact 必须有证据引用或由用户明确确认。",
      ),
    );
  }

  if (statement.statementType === "inference" && statement.status !== "draft") {
    if (statement.supportingFactIds.length === 0) {
      issues.push(
        issue(
          RuleErrorCode.INFERENCE_REQUIRES_FACT,
          "supportingFactIds",
          "inference 必须指向至少一个 supporting fact。",
        ),
      );
    }
    for (const factId of statement.supportingFactIds) {
      const support = context.statementsById?.get(factId);
      if (support !== undefined && support.statementType !== "fact") {
        issues.push(
          issue(
            RuleErrorCode.INFERENCE_SUPPORT_MUST_BE_FACT,
            "supportingFactIds",
            `支持陈述 ${factId} 不是 fact。`,
          ),
        );
      }
    }
  }
  if (
    statement.statementType === "inference" &&
    context.forExternalExport === true &&
    statement.userConfirmation !== "confirmed" &&
    statement.userConfirmation !== "corrected"
  ) {
    issues.push(
      issue(
        RuleErrorCode.UNCONFIRMED_INFERENCE_EXPORT_FORBIDDEN,
        "userConfirmation",
        "未确认 inference 不得作为用户已承认事实导出。",
      ),
    );
  }

  if (
    statement.statementType === "hypothesis" &&
    statement.status !== "draft" &&
    !statement.verificationCondition &&
    !statement.falsificationCondition
  ) {
    issues.push(
      issue(
        RuleErrorCode.HYPOTHESIS_REQUIRES_TEST,
        "verificationCondition",
        "hypothesis 必须包含验证条件或反证条件。",
      ),
    );
  }

  if (
    statement.statementType === "recommendation" &&
    statement.status !== "draft" &&
    statement.basedOnStatementIds.length === 0 &&
    statement.principleIds.length === 0
  ) {
    issues.push(
      issue(
        RuleErrorCode.RECOMMENDATION_REQUIRES_BASIS,
        "basedOnStatementIds",
        "recommendation 必须引用事实、推断或宪法原则。",
      ),
    );
  }

  if (
    statement.confidence !== undefined &&
    statement.confidence > 0 &&
    statement.evidenceRefs.length === 0 &&
    statement.statementType === "fact" &&
    statement.userConfirmation === "unconfirmed"
  ) {
    issues.push(
      issue(
        RuleErrorCode.CONFIDENCE_CANNOT_REPLACE_EVIDENCE,
        "confidence",
        "置信度不能替代证据或用户确认。",
      ),
    );
  }

  return issues;
}

export function validatePrivateSourceRecord(value: {
  schemaVersion?: string;
  exportAllowed?: boolean;
}): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!value.schemaVersion) {
    issues.push(
      issue(
        RuleErrorCode.SCHEMA_VERSION_REQUIRED,
        "schemaVersion",
        "所有记录必须包含 schemaVersion。",
      ),
    );
  }
  if (value.exportAllowed !== false) {
    issues.push(
      issue(
        RuleErrorCode.PRIVATE_EXPORT_FORBIDDEN,
        "exportAllowed",
        "私人原文必须固定为 exportAllowed=false。",
      ),
    );
  }
  return issues;
}

export function validateExternalSummary(
  summary: ExternalSummaryRecord,
  privateSourceIds: ReadonlySet<string>,
): ValidationIssue[] {
  const issues = validateEntityMetadata(summary);
  const serialized = JSON.stringify(summary.payload).toLowerCase();
  if (serialized.includes("rawtext") || serialized.includes("raw_text")) {
    issues.push(
      issue(
        RuleErrorCode.PRIVATE_TEXT_IN_EXTERNAL_SUMMARY,
        "payload",
        "外部摘要不得包含私人原文字段。",
      ),
    );
  }
  if (summary.sourceStructuredRecordIds.some((id) => privateSourceIds.has(id))) {
    issues.push(
      issue(
        RuleErrorCode.EXTERNAL_SUMMARY_PRIVATE_REFERENCE,
        "sourceStructuredRecordIds",
        "外部摘要只能引用结构化记录，不能引用私人原文。",
      ),
    );
  }
  if (summary.source.kind !== "derived" || summary.isIndependentFactSource !== false) {
    issues.push(
      issue(
        RuleErrorCode.EXTERNAL_SUMMARY_MUST_BE_DERIVED,
        "source",
        "外部摘要必须明确标记为派生对象而非独立事实源。",
      ),
    );
  }
  return issues;
}

export function validateSingleActiveCorrection(
  candidate: BehaviorCorrection,
  existing: readonly BehaviorCorrection[],
): ValidationIssue[] {
  if (candidate.correctionStatus !== "active") {
    return [];
  }
  const conflict = existing.some(
    (item) =>
      item.id !== candidate.id &&
      item.effectiveDate === candidate.effectiveDate &&
      item.correctionStatus === "active" &&
      item.deletionState === "active",
  );
  return conflict
    ? [
        issue(
          RuleErrorCode.ONE_ACTIVE_CORRECTION_PER_DAY,
          "effectiveDate",
          "同一业务日期只能有一个当前有效行为纠偏。",
        ),
      ]
    : [];
}

export function validateNewIdea(candidate: IdeaParkingItem): ValidationIssue[] {
  if (!candidate.activationReview && candidate.parkingStatus !== "parked") {
    return [
      issue(
        RuleErrorCode.IDEA_MUST_START_IN_PARKING,
        "parkingStatus",
        "新想法必须从 parked 状态开始，不能自动立项。",
      ),
    ];
  }
  return [];
}

export function validatePromise(entry: PromiseEntry): ValidationIssue[] {
  if (
    !entry.counterpartyAlias.trim() ||
    !entry.content.trim() ||
    !entry.promiseStatus ||
    !entry.dueAt ||
    !entry.businessTimezone
  ) {
    return [
      issue(RuleErrorCode.PROMISE_REQUIRED_FIELDS, "promise", "承诺必须包含对象、内容和状态。"),
    ];
  }
  return [];
}

export function validateDecision(entry: DecisionJournalEntry): ValidationIssue[] {
  if (!entry.decision.trim() || entry.informationAtDecision.length === 0) {
    return [
      issue(
        RuleErrorCode.DECISION_INFORMATION_REQUIRED,
        "informationAtDecision",
        "决策必须保留当时信息，不能只记录结果。",
      ),
    ];
  }
  return [];
}

export function validateConstitutionWrite(
  candidate: ConstitutionVersion,
  previous?: ConstitutionVersion,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (
    candidate.status === "active" &&
    (!candidate.approvedByUserAt || candidate.source.kind === "ai_assisted")
  ) {
    issues.push(
      issue(
        RuleErrorCode.CONSTITUTION_USER_APPROVAL_REQUIRED,
        "approvedByUserAt",
        "宪法版本只有在用户明确批准后才能激活。",
      ),
    );
  }
  if (
    candidate.source.kind === "ai_assisted" &&
    (previous === undefined ||
      JSON.stringify(candidate.principles) !== JSON.stringify(previous.principles))
  ) {
    issues.push(
      issue(
        RuleErrorCode.CONSTITUTION_AI_MUTATION_FORBIDDEN,
        "principles",
        "AI 不得创建或修改生效宪法原则。",
      ),
    );
  }
  return issues;
}

export function validateBoardContextImmutable(
  previous: unknown,
  candidate: unknown,
): ValidationIssue[] {
  return JSON.stringify(previous) === JSON.stringify(candidate)
    ? []
    : [
        issue(
          RuleErrorCode.BOARD_CONTEXT_IMMUTABLE,
          "boardContext",
          "已导入的 BoardContext 不得在 CEO OS 内原地修改。",
        ),
      ];
}

export function validateJointCorrection(
  joint: JointCorrection,
  availableBoardContextIds: ReadonlySet<string>,
  availableCEOReviewExportIds: ReadonlySet<string>,
): ValidationIssue[] {
  const { payload } = joint;
  const issues: ValidationIssue[] = [];
  if (
    !availableBoardContextIds.has(payload.board_context_id) ||
    !availableCEOReviewExportIds.has(payload.ceo_review_export_id)
  ) {
    issues.push(
      issue(
        RuleErrorCode.JOINT_CORRECTION_SOURCE_REQUIRED,
        "payload",
        "JointCorrection 必须能追溯到 BoardContext 和 CEOReviewExport。",
      ),
    );
  }
  if (payload.derived_only !== true) {
    issues.push(
      issue(
        RuleErrorCode.JOINT_CORRECTION_DERIVED_ONLY,
        "payload.derived_only",
        "JointCorrection 是派生对象，不能成为独立事实源。",
      ),
    );
  }
  return issues;
}

export function materialRevisionRequiresAudit(
  previous: StructuredCEORecord,
  candidate: StructuredCEORecord,
  auditEvent?: AuditEvent,
): ValidationIssue[] {
  const changed = JSON.stringify(previous) !== JSON.stringify(candidate);
  if (!changed) {
    return [];
  }
  const isConfirmedDecision =
    previous.recordType === "decision" && previous.userConfirmation === "confirmed";
  const validAudit =
    auditEvent?.entityId === previous.id &&
    (auditEvent.action === "update" || auditEvent.action === "supersede");
  if (isConfirmedDecision && !validAudit) {
    return [
      issue(
        RuleErrorCode.CONFIRMED_DECISION_AUDIT_REQUIRED,
        "auditEvent",
        "已确认的历史决策不能静默覆盖，必须产生 AuditEvent。",
      ),
    ];
  }
  if (previous.userConfirmation === "confirmed" && !validAudit) {
    return [
      issue(
        RuleErrorCode.MAJOR_REVISION_AUDIT_REQUIRED,
        "auditEvent",
        "已确认记录的重大修订必须产生 AuditEvent。",
      ),
    ];
  }
  return [];
}

export function assertEvidenceStatement(
  statement: EvidenceStatement,
  context?: StatementValidationContext,
): void {
  assertNoIssues(validateEvidenceStatement(statement, context));
}

export function assertPrivateSourceRecord(value: {
  schemaVersion?: string;
  exportAllowed?: boolean;
}): void {
  assertNoIssues(validatePrivateSourceRecord(value));
}
